import type { ReactNode } from "react";

import type { ProductionHistoryWindow } from "@/features/devices/types";
import { IconSettings } from "@/lib/icons";
import { WidgetMenu } from "./WidgetShell";

export type HeroTone = "ok" | "muted" | "warn" | "danger";

interface HeroCardProps {
  /** Classe de especializacao para faces que precisam reagir ao proprio tamanho. */
  className?: string;
  /** Nome humano da capacidade (ex.: "Produção da rama"). */
  title: string;
  /** Legenda discreta abaixo do título (ex.: "Metragem acumulada"). */
  caption?: string | null;
  /** Informacao contextual opcional, por exemplo qualidade de resumo por turno. */
  headerAccessory?: ReactNode;
  /** Tom geral — só um realce sutil de borda; nada de fundo gritante. */
  tone?: "ok" | "warn" | "danger";
  /**
   * Alarme visual: quando true E `tone === "danger"`, pulsa a borda para
   * chamar a atenção sem som (mesma ideia do shell legado).
   */
  alarm?: boolean;
  /** Rodapé discreto (ex.: "atualizado há 2s"). Omitir quando nada a dizer. */
  footnote?: ReactNode;
  /** Modo edição: revela a engrenagem de configurar. */
  editable?: boolean;
  onConfigure?: () => void;
  /**
   * Em Visualização, quando definido, mostra o menu `…` com "Gerar gráfico"
   * (só para sinais numéricos; o caller decide quando passar).
   */
  onGenerateChart?: () => void;
  /**
   * Desfaz o "Gerar gráfico" e devolve o card ao tipo de origem. O caller só
   * passa quando o widget carrega a marca de origem.
   */
  onHideChart?: () => void;
  children: ReactNode;
}

/**
 * Casca do "cartão herói" — a nova linguagem de widget (leveza, hierarquia,
 * silêncio quando está tudo bem). Deliberadamente separada do `WidgetShell`
 * legado. Sem caixa alta, sem pilhas de badges; um herói que respira e fala
 * só quando precisa.
 */
export function HeroCard({
  className,
  title,
  caption,
  headerAccessory,
  tone = "ok",
  alarm = false,
  footnote,
  editable,
  onConfigure,
  onGenerateChart,
  onHideChart,
  children,
}: HeroCardProps) {
  const toneClass = tone === "warn" ? "w-hero--warn" : tone === "danger" ? "w-hero--danger" : "";
  const alarmClass = alarm && tone === "danger" ? "w-hero--alarm" : "";
  return (
    <article className={["w-hero", className, toneClass, alarmClass].filter(Boolean).join(" ")}>
      <header className="w-hero__hd">
        <div className="w-hero__titles">
          <h3 className="w-hero__title" title={title}>
            {title}
          </h3>
          {caption ? (
            <p className="w-hero__caption" title={caption}>
              {caption}
            </p>
          ) : null}
        </div>
        {headerAccessory}
        {editable && onConfigure ? (
          <button
            type="button"
            className="iconbtn cfg"
            style={{ width: 24, height: 24 }}
            onClick={onConfigure}
            aria-label={`Configurar ${title}`}
            title={`Configurar ${title}`}
          >
            <IconSettings size={14} />
          </button>
        ) : onGenerateChart || onHideChart ? (
          <WidgetMenu
            title={title}
            onGenerateChart={onGenerateChart}
            onHideChart={onHideChart}
          />
        ) : null}
      </header>

      {children}

      {footnote ? <p className="w-hero__foot">{footnote}</p> : null}
    </article>
  );
}

const WINDOW_OPTIONS: Array<{ value: ProductionHistoryWindow; label: string }> = [
  { value: "6h", label: "6h" },
  { value: "12h", label: "12h" },
  { value: "24h", label: "24h" },
  { value: "48h", label: "48h" },
  { value: "7d", label: "7d" },
  { value: "30d", label: "30d" },
];

/**
 * Seletor de período embutido no card (6h · 12h · 24h · 48h · 7d · 30d). Fica no header
 * da tendência, à direita do rótulo — trocar a janela aqui persiste no widget
 * sem abrir o editor. `busy` liga um spinner discreto enquanto a nova janela
 * carrega; o card continua mostrando a série anterior (estado `reloading` dos
 * hooks), nunca em branco.
 */
export function HeroWindowPicker({
  value,
  busy = false,
  onChange,
  ariaLabel,
}: {
  value: ProductionHistoryWindow;
  busy?: boolean;
  onChange: (window: ProductionHistoryWindow) => void;
  ariaLabel: string;
}) {
  return (
    <span className="winpick" role="group" aria-label={ariaLabel}>
      {busy ? <span className="winpick__spin" aria-hidden="true" /> : null}
      {WINDOW_OPTIONS.map((option) => (
        <button
          key={option.value}
          type="button"
          className={option.value === value ? "on" : undefined}
          aria-pressed={option.value === value}
          onClick={() => {
            if (option.value !== value) onChange(option.value);
          }}
        >
          {option.label}
        </button>
      ))}
    </span>
  );
}

/**
 * Linha de significado: um ponto colorido + uma frase em sentence case. A cor
 * semântica aparece só quando há um julgamento (rodando/parada/sem leitura);
 * neutro fica cinza. É o "o que isto quer dizer" para um diretor.
 */
export function HeroMeaning({
  tone,
  animated = false,
  redundant = false,
  children,
}: {
  tone: HeroTone;
  /** Ponto pulsante (vivo / aguardando telemetria). */
  animated?: boolean;
  /**
   * A frase apenas REPETE o que o valor acima ja diz ("Ligado" / "Saida
   * ativa"). Marcada assim, ela pode ser suprimida onde o espaco e caro sem
   * esconder nada — enquanto uma frase que carrega informacao propria ("Sem
   * leitura recente", "Desligado alem do tolerado") continua aparecendo.
   */
  redundant?: boolean;
  children: ReactNode;
}) {
  return (
    <p
      className={`w-hero__meaning w-hero__meaning--${tone}`}
      data-redundant={redundant || undefined}
    >
      <span
        className={animated ? "w-hero__dot w-hero__dot--anim" : "w-hero__dot"}
        aria-hidden="true"
      />
      <span>{children}</span>
    </p>
  );
}
